import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { AnimatedViews, Container, HStack, ProductCardV2, theme, useResponsive } from '@sg/garnish';

import { MenuHeader } from '@order/components-menu';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { ScreenHeader } from '../../../ScreenHeader';
import { useMenuGridColumns } from '../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuContentLoading = () => {
  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return <MenuCategoriesListLoading />;
  }

  return <MenuGridLoading />;
};

// ─── Components ──────────────────────────────────────────────────────────────

const MenuCategoriesListLoading = () => {
  const isLoggedIn = useIsLoggedIn();

  const placeholderIndexes = [...Array(PLACEHOLDER_CATEGORIES).keys()];

  return (
    <>
      <ScreenHeader rounded={false} />

      <View style={styles.categoriesList}>
        {isLoggedIn ? <AnimatedViews.Pulse style={styles.recentOrdersButton} /> : null}

        {placeholderIndexes.map((index) => (
          <AnimatedViews.Pulse key={index} index={index + 1} style={styles.categoryItem}>
            <View style={styles.categoryImage} />

            <View style={styles.categoryDetails}>
              <View style={styles.categoryTitle} />
              <View style={styles.categoryDescription} />
            </View>
          </AnimatedViews.Pulse>
        ))}
      </View>
    </>
  );
};

const MenuGridLoading = () => {
  const { currentBreakpoint } = useResponsive();
  const itemsPerRow = useMenuGridColumns();

  const placeholderIndexes = [...Array(itemsPerRow * PLACEHOLDER_ROWS).keys()];

  return (
    <ScrollView scrollEnabled={false} style={styles.container}>
      <MenuHeader.Container withoutTopSpacing>
        <AnimatedViews.Pulse>
          <MenuHeader.ContentContainer>
            <View style={styles.locationDetails} />
            <View style={styles.changeLocationButton} />
            <View style={styles.moreInfoButton} />
          </MenuHeader.ContentContainer>

          <Container removeSpacing={currentBreakpoint.isLG} style={styles.categoriesNav}>
            <View style={styles.activeCategoryChip} />

            {CATEGORY_CHIP_WIDTHS.map((width, index) => (
              <CategoryChipPlaceholder key={index} width={width} />
            ))}
          </Container>
        </AnimatedViews.Pulse>
      </MenuHeader.Container>

      <Container style={styles.category}>
        <AnimatedViews.Pulse index={1} style={styles.categoryHeading} />

        <HStack itemsPerRow={itemsPerRow} gap={theme.spacing['4']}>
          {placeholderIndexes.map((index) => (
            <ProductCardPlaceholder key={index} index={index + 2} />
          ))}
        </HStack>
      </Container>
    </ScrollView>
  );
};

const CategoryChipPlaceholder = (props: CategoryChipPlaceholderProps) => {
  const { width } = props;

  const chipDynamicStyle = useStyle(() => ({ width }), [width]);
  const chipStyles = [styles.categoryChip, chipDynamicStyle];

  return <View style={chipStyles} />;
};

const ProductCardPlaceholder = (props: ProductCardPlaceholderProps) => {
  const { index } = props;

  return (
    <ProductCardV2.Container canHover={false}>
      <AnimatedViews.Pulse index={index} style={styles.productContent}>
        <View style={styles.productImageArea}>
          <View style={styles.productImage} />
        </View>

        <View style={styles.productLabelRow}>
          <View style={styles.productLabel} />
        </View>

        <View style={styles.productHeading} />

        <View>
          <View style={styles.productDescriptionLineFull} />
          <View style={styles.productDescriptionLineLong} />
          <View style={styles.productDescriptionLineShort} />
        </View>

        <View style={styles.productNutrition}>
          <View style={styles.productMacros} />
          <View style={styles.productCalories} />
        </View>

        <View style={styles.productFooter}>
          <View style={styles.productPrice} />
        </View>
      </AnimatedViews.Pulse>
    </ProductCardV2.Container>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_CATEGORIES = 6;
const PLACEHOLDER_ROWS = 2;
const CATEGORY_CHIP_WIDTHS = [98, 44, 59, 41, 44, 98, 56];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  categoriesList: {
    flex: 1,
    overflow: 'hidden',
    rowGap: theme.spacing['4'],
    paddingTop: theme.spacing['4'],
    paddingHorizontal: theme.spacing['4'],
  },
  recentOrdersButton: {
    height: 40,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  categoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['4'],
  },
  categoryImage: {
    width: 120,
    height: 120,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  categoryDetails: {
    flex: 1,
  },
  categoryTitle: {
    width: '64%',
    height: 20,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  categoryDescription: {
    width: '80%',
    height: 12,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  locationDetails: {
    width: 320,
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  changeLocationButton: {
    width: 132,
    height: 32,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  moreInfoButton: {
    width: 91,
    height: 32,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  categoriesNav: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 28,
    overflow: 'hidden',
  },
  activeCategoryChip: {
    width: 105,
    height: 32,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  categoryChip: {
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  category: {
    paddingTop: theme.spacing['14'],
  },
  categoryHeading: {
    width: 240,
    height: 32,
    marginTop: 4,
    marginBottom: 28,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productContent: {
    flexGrow: 1,
    rowGap: theme.spacing['1'],
  },
  productImageArea: {
    height: 343,
    paddingTop: theme.spacing['2'],
    paddingBottom: theme.spacing['4'],
  },
  productImage: {
    flex: 1,
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  productLabelRow: {
    minHeight: 22,
    justifyContent: 'flex-end',
  },
  productLabel: {
    width: 40,
    height: 12,
    marginBottom: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productHeading: {
    width: '72%',
    height: 20,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productDescriptionLineFull: {
    width: '100%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productDescriptionLineLong: {
    width: '90%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productDescriptionLineShort: {
    width: '56%',
    height: 14,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productNutrition: {
    marginTop: 21,
  },
  productMacros: {
    width: '76%',
    height: 12,
    marginVertical: 2,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productCalories: {
    width: 48,
    height: 12,
    marginTop: 7,
    marginBottom: 2,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productFooter: {
    marginTop: 'auto',
    paddingTop: theme.spacing['5'],
  },
  productPrice: {
    width: 56,
    height: 14,
    marginVertical: 2,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CategoryChipPlaceholderProps = {
  width: number;
};

type ProductCardPlaceholderProps = {
  index: number;
};
